import React, { useCallback, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { CONTENT } from '../content';
import {
  AboutCopy,
  ArtistCopy,
  Buy,
  C,
  COMIC_PAGES,
  ContractAddress,
  CtoCopy,
  DrawnFooter,
  DrawnNav,
  EASE_OUT,
  InkCheck,
  InkUnderline,
  MEMES_12,
  NDHAUS_PHOTO,
  ORIGINAL_ART,
  Fold,
  FoldSummary,
  ROADMAP_BLOCKS,
  RoadmapStop,
  RedesignFonts,
  Socials,
  SupplyDonut,
  TITLES,
  TOKEN_FACTS,
  TitleArt,
  VIDEOS,
  Video,
  hover,
} from '../kit';

/**
 * F, "giant Zeus". One blue field with a halftone print. Zeus, huge, stays beside the
 * reader the whole way down and leans toward the cursor; his rainbow tongue is the path
 * that runs down the left of the story and marks each chapter. At the end the memes
 * orbit around the original artwork.
 */

const renderStep = ({ milestone, lines, pill }: RoadmapStop) => (
  <li key={milestone.id} className={milestone.status}>
    <h3>
      {lines.map((l) => (
        <span key={l}>{l} </span>
      ))}
      {pill && <span className="pill">{pill}</span>}
    </h3>
    <p>{milestone.description}</p>
    {milestone.link && (
      <a href={milestone.link.href} target="_blank" rel="noopener noreferrer">
        {milestone.link.label}
      </a>
    )}
  </li>
);

const RedesignF: React.FC = () => {
  const zeusRef = useRef<HTMLDivElement>(null);
  const lean = useCallback((e: React.MouseEvent) => {
    const el = zeusRef.current;
    if (!el) return;
    const x = e.clientX / window.innerWidth - 0.5;
    const y = e.clientY / window.innerHeight - 0.5;
    el.style.setProperty('--lean', `${(x * 6).toFixed(2)}deg`);
    el.style.setProperty('--shift', `${(x * 18).toFixed(1)}px`);
    el.style.setProperty('--lift', `${(y * -10).toFixed(1)}px`);
  }, []);

  return (
    <Field data-design="f" id="top" onMouseMove={lean}>
      <RedesignFonts />
      <DrawnNav color={C.ink} accent={C.yellow} />

      <Duo>
        <Story>
          <Chapter className="hero">
            <h1>
              <TitleArt src={TITLES.zeus.src} alt={TITLES.zeus.alt} $w="min(620px, 88vw)" $tilt={-3} />
            </h1>
            <div className="buy">
              <Buy big />
            </div>
            <ContractAddress tone="dark" />
          </Chapter>

          <Chapter>
            <Video video={VIDEOS.trailer} className="video" />
          </Chapter>

          <Chapter id="about">
            <h2>
              <TitleArt src={TITLES.pepesDog.src} alt={TITLES.pepesDog.alt} $w="min(280px, 62vw)" />
            </h2>
            <div className="with-art">
              <Prose>
                <AboutCopy />
              </Prose>
              <img className="art" src={ORIGINAL_ART} alt="The Happiness of Zeus, the original artwork by ndhaus" />
            </div>
          </Chapter>

          <Chapter>
            <h2>
              <TitleArt src={TITLES.takeOver.src} alt={TITLES.takeOver.alt} $w="min(350px, 74vw)" $tilt={2} />
            </h2>
            <Prose>
              <CtoCopy />
            </Prose>
            <Video video={VIDEOS.buy} className="video" />
          </Chapter>

          <Chapter id="tokenomics">
            <h2>
              <TitleArt src={TITLES.tokenomics.src} alt={TITLES.tokenomics.alt} $w="min(640px, 90vw)" />
            </h2>
            <Facts>
              {TOKEN_FACTS.map((f) => (
                <li key={f.title}>
                  <a href={f.link} target="_blank" rel="noopener noreferrer" className="ink-host">
                    <InkCheck size={30} />
                    <span>
                      <span className="t">
                        {f.title}
                        <InkUnderline color={C.white} width={6} />
                      </span>
                      <span className="v">{f.text}</span>
                    </span>
                  </a>
                </li>
              ))}
            </Facts>
            <p className="verify">{CONTENT.tokenomics.verifyText}</p>
            <SupplyDonut size={340} circColor={C.white} />
          </Chapter>

          <Chapter id="roadmap">
            <h2>
              <TitleArt src={TITLES.roadmap.src} alt={TITLES.roadmap.alt} $w="min(460px, 80vw)" />
            </h2>
            <Steps>
              {ROADMAP_BLOCKS.map((block) =>
                block.kind === 'fold' ? (
                  <li key={block.id} className="fold">
                    <Fold>
                      <FoldSummary block={block} />
                      <ol>{block.stops.map(renderStep)}</ol>
                    </Fold>
                  </li>
                ) : (
                  renderStep(block.stop)
                )
              )}
            </Steps>
          </Chapter>

          <Chapter id="artist">
            <h2>
              <TitleArt src={TITLES.artist.src} alt={TITLES.artist.alt} $w="min(320px, 70vw)" />
            </h2>
            <div className="with-art">
              <Prose>
                <ArtistCopy />
              </Prose>
              <img className="art round" src={NDHAUS_PHOTO} alt="ndhaus holding the original Zeus artwork" />
            </div>
          </Chapter>
        </Story>

        <Beside aria-hidden="true">
          <div className="stick" ref={zeusRef}>
            <img src="/redesign2/scenes/zeus-big.webp" alt="" />
          </div>
        </Beside>
      </Duo>

      <Orbit>
        <h2>
          <TitleArt src={TITLES.memes.src} alt={TITLES.memes.alt} $w="min(340px, 70vw)" />
        </h2>
        <div className="ring">
          <img className="core" src={ORIGINAL_ART} alt="" aria-hidden="true" />
          <ul>
            {MEMES_12.map((m, i) => (
              <li key={m.src} style={{ '--a': `${(360 / MEMES_12.length) * i}deg` } as React.CSSProperties}>
                <span className="arm">
                  <img src={m.src} alt={m.alt} loading="lazy" />
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Orbit>

      <Tail>
        <h2>
          <TitleArt src={TITLES.comic.src} alt={TITLES.comic.alt} $w="min(360px, 76vw)" />
        </h2>
        <p className="sub">{CONTENT.comic.subtitle}</p>
        <a className="comic" href="/comic" aria-label="Read the comic">
          {COMIC_PAGES.map((p, i) => (
            <img key={p} src={p} alt={`Comic page ${i + 1}`} loading="lazy" />
          ))}
        </a>
        <div className="join">
          <Buy big />
          <Socials color={C.ink} size={30} />
        </div>
      </Tail>
      <DrawnFooter color={C.ink} />
    </Field>
  );
};

export default RedesignF;

/* --------------------------------------------------------------------------- */

const RAINBOW = 'linear-gradient(90deg, #ff4d4d 0 16.6%, #ff9f1c 0 33.3%, #ffe347 0 50%, #4cd964 0 66.6%, #2f80ff 0 83.3%, #a55eea 0 100%)';

const Field = styled.div`
  font-family: 'Ubuntu', sans-serif;
  color: ${C.ink};
  background-color: ${C.blue};
  background-image: radial-gradient(rgba(255, 255, 255, 0.16) 1.6px, transparent 1.8px);
  background-size: 16px 16px;
  h2 {
    margin: 0 0 24px;
  }
`;

const Duo = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.62fr);
  max-width: 1360px;
  margin: 0 auto;
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Story = styled.main`
  position: relative;
  padding: 10px clamp(16px, 3vw, 40px) 40px clamp(56px, 7vw, 100px);
  &::before {
    content: '';
    position: absolute;
    top: 40px;
    bottom: 0;
    left: clamp(18px, 3vw, 40px);
    width: 22px;
    background: ${RAINBOW};
    border: 3px solid ${C.ink};
    border-radius: 14px;
  }
  @media (max-width: 1000px) {
    padding-left: 48px;
    &::before {
      left: 12px;
      width: 16px;
    }
  }
`;

const Chapter = styled.section`
  position: relative;
  padding: 40px 0 56px;
  &::before {
    content: '';
    position: absolute;
    top: 52px;
    left: calc(-1 * clamp(56px, 7vw, 100px) + clamp(18px, 3vw, 40px) - 9px);
    width: 34px;
    height: 34px;
    background: ${C.white};
    border: 4px solid ${C.ink};
    border-radius: 50% 46% 52% 48%;
    transition: transform 260ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover::before {
      transform: scale(1.2) rotate(20deg);
      background: ${C.yellow};
    }
  `)}
  &.hero {
    padding-top: 20px;
    min-height: min(720px, calc(100vh - 100px));
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
  &.hero h1 {
    margin: 0 0 26px -10px;
  }
  .buy {
    margin-bottom: 24px;
  }
  .video {
    display: block;
    width: 100%;
    max-width: 820px;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    margin-top: 10px;
    background: ${C.ink};
    border: 4px solid ${C.ink};
    border-radius: 26px 34px 22px 38px / 30px 24px 36px 26px;
    transition: transform 360ms ${EASE_OUT};
  }
  ${hover(css`
    .video:hover {
      transform: rotate(-0.8deg) scale(1.01);
    }
  `)}
  .with-art {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(220px, 300px);
    gap: 34px;
    align-items: start;
  }
  .art {
    width: 100%;
    height: auto;
    border: 4px solid ${C.ink};
    border-radius: 44% 56% 50% 50% / 50% 46% 54% 50%;
    transform: rotate(4deg);
    transition: transform 360ms ${EASE_OUT};
  }
  ${hover(css`
    .art:hover {
      transform: rotate(-2deg) scale(1.05);
    }
  `)}
  .verify {
    margin: 24px 0 30px;
    font-weight: 500;
    font-size: 17px;
  }
  @media (max-width: 1000px) {
    &::before {
      left: -45px;
      width: 28px;
      height: 28px;
    }
    &.hero {
      min-height: 0;
    }
  }
  @media (max-width: 700px) {
    .with-art {
      grid-template-columns: 1fr;
    }
    .art {
      width: min(260px, 70vw);
    }
  }
`;

const Prose = styled.div`
  max-width: 60ch;
  font-size: clamp(17px, 1.3vw, 19px);
  font-weight: 500;
  line-height: 1.62;
  p {
    margin: 0 0 1em;
  }
  a {
    color: inherit;
    font-weight: 700;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }
`;

const Facts = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 46px;
  max-width: 760px;
  margin: 0;
  padding: 0;
  list-style: none;
  a {
    display: flex;
    gap: 12px;
    min-height: 44px;
    color: inherit;
    text-decoration: none;
  }
  .t {
    position: relative;
    display: inline-block;
    font-family: 'GROBOLD', sans-serif;
    font-size: clamp(21px, 2vw, 27px);
    text-transform: uppercase;
  }
  .v {
    display: block;
    margin-top: 6px;
    font-size: 16.5px;
    overflow-wrap: anywhere;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Steps = styled.ol`
  display: grid;
  gap: 26px;
  max-width: 720px;
  margin: 0;
  padding: 0;
  list-style: none;
  ol {
    display: grid;
    gap: 26px;
    margin: 18px 0 0;
    padding: 0;
    list-style: none;
  }
  li.fold {
    padding-left: 0;
    border-left: 0;
  }
  li {
    position: relative;
    padding-left: 22px;
    border-left: 5px solid ${C.ink};
    transition: transform 240ms ${EASE_OUT}, border-color 240ms ${EASE_OUT};
  }
  li.planned {
    border-left-style: dashed;
  }
  li.in-progress {
    border-left: 10px solid ${C.yellow};
  }
  li.in-progress h3 {
    font-size: clamp(24px, 2.2vw, 30px);
  }
  ${hover(css`
    li:hover {
      transform: translateX(6px);
      border-left-color: ${C.yellow};
    }
  `)}
  h3 {
    margin: 0 0 6px;
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: clamp(19px, 1.6vw, 23px);
    line-height: 1.25;
  }
  .pill {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 12px;
    font-size: 14px;
    vertical-align: middle;
    background: ${C.yellow};
    border: 2.5px solid ${C.ink};
    border-radius: 999px;
    transform: rotate(-3deg);
  }
  p {
    margin: 0;
    font-size: 17px;
    font-weight: 500;
    line-height: 1.6;
  }
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: inherit;
    font-weight: 700;
  }
`;

const Beside = styled.aside`
  position: relative;
  .stick {
    position: sticky;
    top: 0;
    display: flex;
    align-items: flex-end;
    height: 100vh;
    padding-bottom: 20px;
    --lean: 0deg;
    --shift: 0px;
    --lift: 0px;
  }
  img {
    width: 100%;
    height: auto;
    max-height: 92vh;
    object-fit: contain;
    transform-origin: 50% 95%;
    transform: translate(var(--shift), var(--lift)) rotate(var(--lean));
    transition: transform 500ms ${EASE_OUT};
  }
  @media (max-width: 1000px) {
    display: none;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const counter = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
`;

const Orbit = styled.section`
  position: relative;
  padding: 60px 16px 40px;
  text-align: center;
  h2 {
    display: flex;
    justify-content: center;
  }
  .ring {
    position: relative;
    width: min(820px, 92vw);
    aspect-ratio: 1;
    margin: 0 auto;
  }
  .core {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 34%;
    height: auto;
    border: 4px solid ${C.ink};
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
  ul {
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    animation: ${spin} 80s linear infinite;
  }
  li {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 17%;
    aspect-ratio: 1;
    margin: -8.5% 0 0 -8.5%;
    transform: rotate(var(--a)) translateY(-205%) rotate(calc(-1 * var(--a)));
  }
  .arm {
    display: block;
    width: 100%;
    height: 100%;
    animation: ${counter} 80s linear infinite;
  }
  .arm img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: 3px solid ${C.ink};
    border-radius: 50%;
    transition: transform 280ms ${EASE_OUT}, border-radius 280ms ${EASE_OUT};
  }
  ${hover(css`
    .ring:hover ul,
    .ring:hover .arm {
      animation-play-state: paused;
    }
    li:hover {
      z-index: 2;
    }
    li:hover .arm img {
      transform: scale(1.9);
      border-radius: 18px;
    }
  `)}
  @media (max-width: 700px) {
    .ring {
      aspect-ratio: auto;
    }
    .core {
      position: static;
      display: block;
      width: 60%;
      margin: 0 auto 20px;
      transform: none;
    }
    ul {
      position: static;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      animation: none;
    }
    li {
      position: static;
      width: auto;
      margin: 0;
      transform: none;
    }
    .arm {
      animation: none;
    }
    .arm img {
      border-radius: 20px;
    }
  }
`;

const Tail = styled.section`
  display: grid;
  justify-items: center;
  gap: 10px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 40px 16px 50px;
  text-align: center;
  .sub {
    max-width: 620px;
    margin: -6px 0 28px;
    font-size: 17px;
    line-height: 1.55;
  }
  .comic {
    display: flex;
    justify-content: center;
  }
  .comic img {
    width: min(240px, 28vw);
    height: auto;
    margin: 0 -10px;
    border: 3px solid ${C.ink};
    transition: transform 300ms ${EASE_OUT};
  }
  .comic img:nth-child(1) { transform: rotate(-6deg); }
  .comic img:nth-child(2) { position: relative; z-index: 1; transform: translateY(-12px); }
  .comic img:nth-child(3) { transform: rotate(6deg); }
  ${hover(css`
    .comic:hover img:nth-child(1) { transform: rotate(-10deg) translateX(-18px); }
    .comic:hover img:nth-child(2) { transform: translateY(-24px) scale(1.04); }
    .comic:hover img:nth-child(3) { transform: rotate(10deg) translateX(18px); }
  `)}
  .join {
    display: grid;
    justify-items: center;
    gap: 20px;
    margin-top: 60px;
  }
`;
